import React, { useState, useEffect } from 'react';
import { Smartphone, Monitor, Share, Plus, Download, ArrowRight, X, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';

const PWAInstallGuide = ({ onComplete, onSkip }) => {
  const [deviceType, setDeviceType] = useState('unknown');
  const [isStandalone, setIsStandalone] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstructions, setShowInstructions] = useState(true);

  useEffect(() => {
    // Detect if app is already installed (running as PWA)
    const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || 
                             window.navigator.standalone === true;
    setIsStandalone(isStandaloneMode);

    // If already installed, skip this screen
    if (isStandaloneMode) {
      onComplete();
      return;
    }

    // Detect device type
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      setDeviceType('ios');
    } else if (/android/i.test(userAgent)) {
      setDeviceType('android');
    } else if (/Windows|Mac|Linux/.test(userAgent)) {
      setDeviceType('desktop');
    } else {
      setDeviceType('android'); // Default to Android instructions
    }

    // Listen for the beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [onComplete]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Fallback to showing instructions
      setShowInstructions(true);
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
      onComplete();
    }
    
    setDeferredPrompt(null);
  };

  const renderIOSInstructions = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-center mb-6">
        <div className="w-20 h-20 bg-gradient-to-br from-rose-400 to-rose-600 rounded-2xl flex items-center justify-center shadow-lg">
          <Smartphone className="w-10 h-10 text-white" />
        </div>
      </div>

      <h2 className="text-2xl font-bold text-center text-gray-800 mb-2">
        Add to Your Home Screen
      </h2>
      <p className="text-center text-gray-600 mb-8">
        Get the full app experience with quick access from your home screen
      </p>

      {/* Step-by-step instructions with visual cues */}
      <div className="space-y-4">
        <div className="flex items-start space-x-4 p-4 bg-white rounded-xl border-2 border-rose-100">
          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
            <span className="text-white font-bold">1</span>
          </div>
          <div className="flex-1">
            <p className="text-gray-800 font-medium mb-2">Tap the Share button</p>
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center border border-blue-200">
                <Share className="w-5 h-5 text-blue-600" />
              </div>
              <span>in your Safari browser (bottom or top bar)</span>
            </div>
          </div>
        </div>

        <div className="flex items-start space-x-4 p-4 bg-white rounded-xl border-2 border-rose-100">
          <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
            <span className="text-white font-bold">2</span>
          </div>
          <div className="flex-1">
            <p className="text-gray-800 font-medium mb-2">Select "Add to Home Screen"</p>
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center border border-purple-200">
                <Plus className="w-5 h-5 text-purple-600" />
              </div>
              <span>Scroll down in the menu if needed</span>
            </div>
          </div>
        </div>

        <div className="flex items-start space-x-4 p-4 bg-white rounded-xl border-2 border-rose-100">
          <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
            <span className="text-white font-bold">3</span>
          </div>
          <div className="flex-1">
            <p className="text-gray-800 font-medium mb-2">Tap "Add" to confirm</p>
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center border border-green-200">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <span>The app will appear on your home screen</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 p-4 bg-rose-50 rounded-xl border border-rose-200">
        <p className="text-sm text-rose-800 text-center">
          💡 Once installed, you'll have instant access to all your self-love tools!
        </p>
      </div>
    </div>
  );

  const renderAndroidInstructions = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-center mb-6">
        <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl flex items-center justify-center shadow-lg">
          <Smartphone className="w-10 h-10 text-white" />
        </div>
      </div>

      <h2 className="text-2xl font-bold text-center text-gray-800 mb-2">
        Install Constant Love App
      </h2>
      <p className="text-center text-gray-600 mb-8">
        Get instant access with a home screen icon
      </p>

      {deferredPrompt ? (
        // Show install button if prompt is available
        <div className="space-y-4">
          <Button
            onClick={handleInstallClick}
            className="w-full h-14 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold text-lg shadow-lg"
          >
            <Download className="w-5 h-5 mr-2" />
            Install Now
          </Button>
          
          <p className="text-sm text-center text-gray-500">
            Tap the button above to install the app instantly
          </p>
        </div>
      ) : (
        // Show manual instructions if prompt is not available
        <div className="space-y-4">
          <div className="flex items-start space-x-4 p-4 bg-white rounded-xl border-2 border-green-100">
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
              <span className="text-white font-bold">1</span>
            </div>
            <div className="flex-1">
              <p className="text-gray-800 font-medium mb-2">Tap the menu (⋮)</p>
              <p className="text-sm text-gray-600">
                Usually found in the top-right corner of your browser
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-4 p-4 bg-white rounded-xl border-2 border-green-100">
            <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
              <span className="text-white font-bold">2</span>
            </div>
            <div className="flex-1">
              <p className="text-gray-800 font-medium mb-2">Select "Add to Home screen" or "Install app"</p>
              <div className="flex items-center space-x-2 text-sm text-gray-600 mt-2">
                <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center border border-purple-200">
                  <Download className="w-5 h-5 text-purple-600" />
                </div>
                <span>Look for the install option</span>
              </div>
            </div>
          </div>

          <div className="flex items-start space-x-4 p-4 bg-white rounded-xl border-2 border-green-100">
            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
              <span className="text-white font-bold">3</span>
            </div>
            <div className="flex-1">
              <p className="text-gray-800 font-medium mb-2">Tap "Install" to confirm</p>
              <p className="text-sm text-gray-600">
                The app will be added to your home screen
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 p-4 bg-green-50 rounded-xl border border-green-200">
        <p className="text-sm text-green-800 text-center">
          🚀 Enjoy faster loading and offline access to your self-love journey!
        </p>
      </div>
    </div>
  );

  const renderDesktopInstructions = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-center mb-6">
        <div className="w-20 h-20 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
          <Monitor className="w-10 h-10 text-white" />
        </div>
      </div>

      <h2 className="text-2xl font-bold text-center text-gray-800 mb-2">
        Install on Your Computer
      </h2>
      <p className="text-center text-gray-600 mb-8">
        Access Constant Love directly from your desktop
      </p>

      <div className="space-y-4">
        <div className="flex items-start space-x-4 p-4 bg-white rounded-xl border-2 border-indigo-100">
          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
            <span className="text-white font-bold">1</span>
          </div>
          <div className="flex-1">
            <p className="text-gray-800 font-medium mb-2">Look for the install icon</p>
            <p className="text-sm text-gray-600">
              In Chrome/Edge: Click the install icon (⊕) in the address bar
              <br />In Safari: File → Add to Dock
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-4 p-4 bg-white rounded-xl border-2 border-indigo-100">
          <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
            <span className="text-white font-bold">2</span>
          </div>
          <div className="flex-1">
            <p className="text-gray-800 font-medium mb-2">Click "Install"</p>
            <p className="text-sm text-gray-600">
              The app will be installed and added to your applications
            </p>
          </div>
        </div>
      </div>

      <div className="mt-8 p-4 bg-indigo-50 rounded-xl border border-indigo-200">
        <p className="text-sm text-indigo-800 text-center">
          💻 Launch Constant Love like any other app on your computer!
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-rose-100 flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full p-8 shadow-2xl">
        {/* Skip button */}
        <button
          onClick={onSkip}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Instructions based on device */}
        {deviceType === 'ios' && renderIOSInstructions()}
        {deviceType === 'android' && renderAndroidInstructions()}
        {deviceType === 'desktop' && renderDesktopInstructions()}

        {/* Action buttons */}
        <div className="mt-8 space-y-3">
          <Button
            onClick={onComplete}
            className="w-full h-12 bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white font-semibold shadow-lg"
          >
            Continue to App
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          
          <button
            onClick={onSkip}
            className="w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            I'll do this later
          </button>
        </div>
      </Card>
    </div>
  );
};

export default PWAInstallGuide;
